import { leadSourceOptionsSelector, leadStagesSelector } from '@repo/config-services';
import NumberRangeSelect from 'advanced-filters/components/NumberRangeSelect';
import TimeFilter from 'advanced-filters/components/TimeFilter';
import {
  FILTERABLE_ENTITY_TYPES, type FilterableEntityTypes,
} from 'advanced-filters/data';
import { ALL_COMPARATORS, COMPARATORS_WITHOUT_VALUE, type ComparatorType } from 'advanced-filters/data/comparators';
import { ALL_DATA_TYPES, type DataTypeType } from 'advanced-filters/data/dataTypes';
import { type KeyValueType, type TimeFilterOptionsType } from 'advanced-filters/store/filter';
import { Input, Select } from 'antd';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

export interface DropdownInputProps {
  remoteSearchable?: boolean;
  searchValue?: string;
  onSearch?: (value: string) => void;
}

function DropdownInput({
  context,
  entityType,
  value,
  options,
  handleChange,
  dropdownProps,
}: {
  context: 'condition' | 'subCondition';
  entityType: FilterableEntityTypes;
  value: KeyValueType | KeyValueType[] | undefined;
  options: { label: string; value: string }[];
  handleChange: (args: KeyValueType) => void;
  dropdownProps?: DropdownInputProps;
}) {
  let mode: 'multiple' | undefined = 'multiple';
  if (context === 'condition' && (
    entityType === FILTERABLE_ENTITY_TYPES.leadActivity || entityType === FILTERABLE_ENTITY_TYPES.taskType
  )) {
    mode = undefined;
  }
  const handleSelect = useCallback((selectedOptionId: any) => {
    if (mode === 'multiple') {
      const selectedOptions = options.filter((option) => selectedOptionId.includes(option.value));
      handleChange({ value: selectedOptions });
    } else {
      const selectedOption = options.find((option) => selectedOptionId === option.value);
      handleChange({ value: [selectedOption || { value: selectedOptionId }] });
    }
  }, [handleChange, options, mode]);

  const selectedValues = Array.isArray(value) ? value.map((v) => v?.value || v) : value?.value;

  const handleSearch = useCallback((searchValue: string) => {
    if (dropdownProps?.onSearch) {
      dropdownProps.onSearch(searchValue);
    }
  }, [dropdownProps]);

  if (dropdownProps?.remoteSearchable) {
    return (
      <Select
        showSearch
        mode={mode}
        placeholder="Select a Value"
        optionFilterProp="label"
        value={selectedValues}
        onChange={handleSelect}
        options={options}
        searchValue={dropdownProps.searchValue}
        onSearch={handleSearch}
      />
    );
  } else {
    return (
      <Select
        showSearch
        mode={mode}
        placeholder="Select a Value Or Type to Search"
        optionFilterProp="label"
        value={selectedValues}
        onChange={handleSelect}
        options={options}
      />
    );
  }
}

/**
 * Component to take input based on the inputDataType
 * @returns {JSX.Element} - Based on the inputDataType, it returns the respective input component
 */
export default function DataTypeSpecificInput({
  entityType,
  context,
  value,
  inputDataType,
  comparisionType,
  handleChange,
  options,
  dropdownProps,
}: {
  entityType: FilterableEntityTypes;
  context: 'condition' | 'subCondition';
  value: KeyValueType | undefined;
  inputDataType: DataTypeType;
  comparisionType?: ComparatorType | undefined;
  handleChange: (args: KeyValueType) => void;
  options?: { label: string; value: string }[];
  dropdownProps?: DropdownInputProps;
}) {
  const leadStages = useSelector(leadStagesSelector);
  const leadSourceOptions = useSelector(leadSourceOptionsSelector);

  if (COMPARATORS_WITHOUT_VALUE.includes(comparisionType || '')) {
    return null;
  }

  if (inputDataType === ALL_DATA_TYPES.string) {
    return (
      <Input
        placeholder="Enter a Value"
        value={value?.value}
        onChange={(e) => handleChange({ value: e.target.value })}
      />
    );
  } else if (inputDataType === ALL_DATA_TYPES.number) {
    if (comparisionType === ALL_COMPARATORS.between) {
      return (
        <NumberRangeSelect
          value={value?.value}
          handleChange={handleChange}
        />
      );
    } else {
      return (
        <Input
          placeholder="Enter a Value"
          type="number"
          value={value?.value}
          onChange={(e) => handleChange({ value: e.target.value })}
        />
      );
    }
  } else if (inputDataType === ALL_DATA_TYPES.boolean) {
    // Boolean does not require any input as it is governed by comparator
    return null;
  } else if (inputDataType === ALL_DATA_TYPES.date
    || inputDataType === ALL_DATA_TYPES.datetime
  ) {
    let mode: 'before' | 'after' | 'all' = 'all';
    if (comparisionType === ALL_COMPARATORS.before || comparisionType === ALL_COMPARATORS.after) {
      mode = comparisionType;
    }
    return (
      <TimeFilter
        timeConfig={value?.value?.timeFilter}
        handleChange={(args: TimeFilterOptionsType) => handleChange({ value: { timeFilter: args } })}
        keysMode={mode}
        inputMode={mode}
      />
    );
  } else {
    let newOptions: { label: string; value: string }[] = options || [];
    if (inputDataType === ALL_DATA_TYPES.reference_lead_stage) {
      newOptions = leadStages;
    } else if (inputDataType === ALL_DATA_TYPES.reference_lead_source) {
      newOptions = leadSourceOptions;
    }

    return (
      <DropdownInput
        context={context}
        entityType={entityType}
        value={value?.value}
        options={newOptions}
        handleChange={handleChange}
        dropdownProps={dropdownProps}
      />
    );
  }
}
